import { API_TYPES, useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";

import { type HiringEventInclude } from "../../HiringFeed/api/hiringFeed.schema";
import {
  type CandidateBanInclude,
  type PlacementIncludedApplication,
  type PlacementIncludedInterviewSettings,
  type PlacementIncludedWorkplace,
  type PlacementResponse,
  placementResponseSchema,
  type RequirementInclude,
  type TagInclude,
  type WorkerRequirementInclude,
  type WorkplaceUserInclude,
} from "../types/fetchPlacements.schema";
import { type PlacementWithDetails } from "./useFetchPaginatedPlacements";

export function transformPlacementData(
  placement: PlacementResponse,
  credentialRequirementConfiguration: Set<string>
): PlacementWithDetails {
  const workplace = placement.included.find(
    (included) =>
      included.type === API_TYPES.workplace &&
      included.id === placement.data.relationships.workplace.data.id
  ) as PlacementIncludedWorkplace | undefined;
  if (!workplace) {
    throw new Error("Workplace not found");
  }

  const applications = placement.data.relationships.applications?.data.map((application) => {
    const applicationData = placement.included.find(
      (included) =>
        included.type === API_TYPES.placementApplication && included.id === application.id
    ) as PlacementIncludedApplication | undefined;
    if (!applicationData) {
      throw new Error("Application details not found");
    }

    return applicationData;
  });

  const interviewSettings = placement.included.find(
    (included) =>
      included.type === API_TYPES.interviewSettings &&
      included.relationships.workplace.data.id === placement.data.relationships.workplace.data.id
  ) as PlacementIncludedInterviewSettings | undefined;

  const interviews = placement.included.filter((included) => included.type === API_TYPES.interview);

  const hiringEvents = placement.included.filter(
    (included) =>
      included.type === API_TYPES.hiringEvent &&
      placement.data.relationships.hiringEvents?.data.some(
        (hiringEvent) => hiringEvent.id === included.id
      )
  ) as HiringEventInclude[];

  // Do not assign any worker requirements to the placement if the placement is exempt from document requirements
  const allWorkerRequirements = placement.included
    .filter(
      (included): included is WorkerRequirementInclude =>
        included.type === API_TYPES.workerRequirement &&
        workplace.relationships.workerRequirements.data.some(
          (workerRequirement) => workerRequirement.id === included.id
        ) &&
        included.attributes.qualifications.some((qualification) =>
          placement.data.attributes.workerTypes?.includes(qualification)
        )
    )
    .map((workerRequirement) => {
      const requirement = placement.included.find(
        (included): included is RequirementInclude =>
          included.type === API_TYPES.requirement &&
          included.id === workerRequirement.relationships.requirement.data.id
      );
      if (!requirement) {
        throw new Error("Requirement details not found");
      }

      return {
        ...workerRequirement,
        relationships: {
          ...workerRequirement.relationships,
          requirement: {
            data: {
              ...workerRequirement.relationships.requirement.data,
              attributes: requirement?.attributes,
            },
          },
        },
      };
    });

  const workerRequirements = placement.data.meta.isExemptFromDocumentRequirements
    ? []
    : allWorkerRequirements.filter(
        (workerRequirement) =>
          !credentialRequirementConfiguration.has(
            workerRequirement.relationships.requirement.data.id
          )
      );

  const credentialRequirements = allWorkerRequirements.filter((workerRequirement) =>
    credentialRequirementConfiguration.has(workerRequirement.relationships.requirement.data.id)
  );

  const workerFavoritePlacementId =
    placement.data.relationships.workerFavoritePlacement?.data?.id ?? undefined;

  const tags = placement.data.relationships.tags?.data
    .map((tagRef) => {
      return placement.included.find(
        (included): included is TagInclude => included.type === "tag" && included.id === tagRef.id
      );
    })
    .filter((tag): tag is TagInclude => tag !== undefined);

  const candidateBans = placement.included.filter(
    (included): included is CandidateBanInclude => included.type === API_TYPES.placementCandidateBan
  );

  const hiringManagerRelationships = placement.data.relationships.hiringManagers?.data ?? [];
  const hiringManagers = hiringManagerRelationships
    .map((hiringManagerRelationship) => {
      const hiringManagerData = placement.included.find(
        (included): included is WorkplaceUserInclude =>
          included.type === API_TYPES.workplaceUser && included.id === hiringManagerRelationship.id
      );
      if (!hiringManagerData) {
        return undefined;
      }
      return {
        ...hiringManagerData,
        isPrimary: hiringManagerRelationship.meta?.isPrimary ?? false,
      };
    })
    .filter(
      (manager): manager is WorkplaceUserInclude & { isPrimary: boolean } => manager !== undefined
    );

  return {
    ...placement.data.attributes,
    ...placement.data.meta,
    id: placement.data.id,
    workplace: { ...workplace.attributes, ...workplace.meta, id: workplace.id },
    applications,
    interviewSettings,
    interviews,
    hiringEvents,
    workerRequirements,
    credentialRequirements,
    workerFavoritePlacementId,
    tags,
    candidateBans,
    hiringManagers,
  };
}

export type PlacementDetail = ReturnType<typeof transformPlacementData>;

interface GetPlacementParams {
  placementId: string;
  workerId: string;
}

export function getPlacementUrl(placementId: string, workerId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/placements/${placementId}`;
}

export function useGetPlacement(
  params: GetPlacementParams,
  options?: UseGetQueryOptions<PlacementResponse>
) {
  const { placementId, workerId } = params;
  return useGetQuery({
    url: getPlacementUrl(placementId, workerId),
    queryParams: {
      include: "tags",
    },
    responseSchema: placementResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_PLACEMENT_FAILURE,
      userErrorMessage: "Something went wrong while loading the placement.",
    },
    ...options,
  });
}
